import { square } from '$/services/ui-env';

import { type Square } from '@square/web-payments-sdk-types';

/**
 * On-demand loader for Square's Web Payments SDK.
 *
 * The SDK used to be a synchronous `<script>` in the root `+Head.tsx`, which
 * put ~100 KB and a render-blocking round trip on the critical path of every
 * page — including the home page, which has no payment form at all.
 *
 * It can't simply move to a per-page `+Head.tsx` either: vike-react's `Head` is
 * server-only (`env: { server: true }`), so with `clientRouting` enabled a
 * visitor navigating from the home page to checkout would never get the tag,
 * and the card form would silently fail to attach.
 *
 * So the payment pages ask for it when they mount, and await it. The promise is
 * cached, so concurrent mounts share a single script tag and a single fetch.
 */

const SDK_URL = square.environment === 'sandbox'
  ? 'https://sandbox.web.squarecdn.com/v1/square.js'
  : 'https://web.squarecdn.com/v1/square.js';

let pending: Promise<Square> | null = null;

export function loadSquare(): Promise<Square> {
  if (typeof window === 'undefined')
    return Promise.reject(new Error('Square Web Payments SDK can only be loaded in the browser.'));

  if (window.Square)
    return Promise.resolve(window.Square);

  if (pending)
    return pending;

  pending = new Promise<Square>((resolve, reject) => {
    const script = document.createElement('script');

    script.src = SDK_URL;
    script.async = true;

    script.addEventListener('load', () => {
      if (window.Square)
        resolve(window.Square);
      else
        reject(new Error('Square Web Payments SDK loaded but did not register window.Square.'));
    });

    script.addEventListener('error', () => {
      // Cleared so a later mount (or a retry after the network recovers) is not
      // stuck with this rejected promise forever.
      pending = null;
      reject(new Error(`Failed to load the Square Web Payments SDK from ${SDK_URL}.`));
    });

    document.head.appendChild(script);
  });

  return pending;
}
